import type { ExpressionSpecification, FilterSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import { COLOR_NEUTRAL_WHITE, COLOR_TEXT_PRIMARY, COLOR_TEXT_SECONDARY, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import {
  buildTrailheadCollapsedIconImage,
  buildTrailheadCountTextField,
  buildTrailheadIconCollapsedOffset,
  buildTrailheadIconImage,
  buildTrailheadIconOffset,
  buildTrailheadSelectedIconImage,
  buildTrailheadTextOffset,
  type TrailheadVariantExpressions
} from '../../utils/buildTrailheadIconExpressions';
import { getLayerBeforeId, trailheadsOverlayCollapsedActiveId, trailheadsOverlayCollapsedId, trailheadsOverlayLayerId } from '../../utils/layers';

const activeSortKey = 500;

type TrailheadVectorPinLayerProps = {
  collapsedActiveLayerId?: string;
  collapsedLayerId?: string;
  filter?: ExpressionSpecification;
  isCollapsedExpression: ExpressionSpecification;
  isHoveredExpression: ExpressionSpecification;
  isSelectedExpression: ExpressionSpecification;
  layerId?: string;
  popularityExpression?: ExpressionSpecification | number;
  sourceId: string;
  sourceLayer?: string;
  trailCountExpression: ExpressionSpecification;
} & TrailheadVariantExpressions;

// https://www.figma.com/design/Hfw8SV8IylHR4A3nnQ7Fvs/Map-Icons?node-id=481-1698&p=f&m=dev

const TrailheadVectorPinLayer = ({
  collapsedActiveLayerId = trailheadsOverlayCollapsedActiveId,
  collapsedLayerId = trailheadsOverlayCollapsedId,
  filter,
  isClosedExpression,
  isCollapsedExpression,
  isCompletedExpression,
  isHoveredExpression,
  isSavedExpression,
  isSelectedExpression,
  layerId = trailheadsOverlayLayerId,
  popularityExpression = 0,
  sourceId,
  sourceLayer,
  trailCountExpression
}: TrailheadVectorPinLayerProps) => {
  const map = useMap();
  const variantExpressions = { isClosedExpression, isCompletedExpression, isSavedExpression };
  const isActiveExpression: ExpressionSpecification = ['any', isHoveredExpression, isSelectedExpression];
  const sourceLayerProps = sourceLayer === undefined ? {} : { 'source-layer': sourceLayer };
  const sharedLayoutProps: SymbolLayerSpecification['layout'] = {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-font': ['AllTrails Aeonik Medium', 'Manrope Bold'],
    'text-size': FONT_SIZE_100,
    'text-line-height': 1,
    'symbol-sort-key': ['case', isActiveExpression, activeSortKey, popularityExpression]
  };
  // Cluster tiles come with their own `point_count`, which these pins never draw, since a trailhead
  // already counts its trails.
  const pinFilter = (...conditions: ExpressionSpecification[]) =>
    ['all', ['!', ['has', 'point_count']], ...conditions, ...(filter === undefined ? [] : [filter])] as FilterSpecification;

  return (
    <>
      {/* Expanded pins */}
      <Layer
        beforeId={getLayerBeforeId(map, layerId)}
        filter={pinFilter(['!', isCollapsedExpression])}
        id={layerId}
        layout={{
          ...sharedLayoutProps,
          'icon-image': buildTrailheadIconImage(variantExpressions, isActiveExpression, trailCountExpression),
          'text-field': buildTrailheadCountTextField(isActiveExpression, trailCountExpression),
          'text-offset': buildTrailheadTextOffset(variantExpressions, isActiveExpression),
          'icon-offset': buildTrailheadIconOffset(isActiveExpression)
        }}
        paint={{
          'text-color': ['case', isClosedExpression, COLOR_TEXT_SECONDARY, COLOR_TEXT_PRIMARY],
          'text-halo-color': ['case', isActiveExpression, COLOR_NEUTRAL_WHITE, COLOR_TEXT_SECONDARY],
          'text-halo-width': ['case', isActiveExpression, 1.5, 0]
        }}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
      {/* Collapsed pins. */}
      <Layer
        beforeId={getLayerBeforeId(map, collapsedLayerId)}
        filter={pinFilter(isCollapsedExpression, ['!', isActiveExpression])}
        id={collapsedLayerId}
        layout={{
          ...sharedLayoutProps,
          'icon-image': buildTrailheadCollapsedIconImage(variantExpressions),
          'text-field': ''
        }}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
      {/* A collapsed pin expands to the selected pin on its own layer so it draws over its neighbors. */}
      <Layer
        beforeId={getLayerBeforeId(map, collapsedActiveLayerId)}
        filter={pinFilter(isCollapsedExpression, isActiveExpression)}
        id={collapsedActiveLayerId}
        layout={{
          ...sharedLayoutProps,
          'icon-image': buildTrailheadSelectedIconImage(variantExpressions),
          'text-field': '',
          'icon-offset': buildTrailheadIconCollapsedOffset(isActiveExpression)
        }}
        source={sourceId}
        {...sourceLayerProps}
        type="symbol"
      />
    </>
  );
};

export default TrailheadVectorPinLayer;
